/**
 * Editor for a configurable standard section.
 *
 * Sales may include or exclude approved items, edit their wording, or add one
 * of their own. Each item shows its governance state — STANDARD, MODIFIED or
 * CUSTOM — so it is obvious at a glance how far a proposal has drifted from the
 * approved template, and a modified item can be reverted in one click.
 *
 * Items are only materialised when this editor is first opened. A Quick
 * Proposal never touches it, which is what makes a standard proposal provably
 * standard.
 */
import { useState } from 'react';
import type { AIFunctionId, AIStatus, ConfigurableItem, ItemState } from '../../lib/api';
import { Button, Card, Input } from '../../components/ui';
import { AIAssist } from './AIAssist';

const STATE_STYLE: Record<ItemState, string> = {
  STANDARD: 'bg-tint text-muted',
  MODIFIED: 'bg-amber-50 text-amber-800 ring-1 ring-amber-200',
  CUSTOM: 'bg-sky-50 text-sky-800 ring-1 ring-sky-200',
};

export function StateBadge({ state }: { state: ItemState }) {
  return (
    <span
      className={[
        'inline-flex shrink-0 items-center rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide',
        STATE_STYLE[state],
      ].join(' ')}
    >
      {state}
    </span>
  );
}

/** Derived client-side purely for display; the server re-derives on save. */
function stateOf(item: ConfigurableItem): ItemState {
  if (item.standardValue === null) return 'CUSTOM';
  const same =
    item.value.trim() === (item.standardValue ?? '').trim() &&
    (item.detail ?? '').trim() === (item.standardDetail ?? '').trim();
  return same ? 'STANDARD' : 'MODIFIED';
}

export function ItemEditor({
  title,
  description,
  items,
  onChange,
  disabled,
  hasDetail,
  valueLabel,
  detailLabel,
  addLabel,
  idPrefix,
  onRestoreSection,
  proposalId,
  aiStatus,
  aiFunctions,
}: {
  title: string;
  description: string;
  items: ConfigurableItem[];
  onChange(items: ConfigurableItem[]): void;
  disabled: boolean;
  /** Whether items carry a second field, as deliverables do. */
  hasDetail?: boolean;
  valueLabel: string;
  detailLabel?: string;
  addLabel: string;
  idPrefix: string;
  /** Restore the whole section to the approved baseline. */
  onRestoreSection?(): void;
  proposalId?: string;
  /** Null, or a disabled provider, means no AI controls render. */
  aiStatus?: AIStatus | null;
  /** Which functions suit this section. Omitted means none. */
  aiFunctions?: AIFunctionId[];
}) {
  const [expanded, setExpanded] = useState<string | null>(null);

  const patch = (id: string, changes: Partial<ConfigurableItem>) =>
    onChange(items.map((item) => (item.id === id ? { ...item, ...changes } : item)));

  const revert = (item: ConfigurableItem) =>
    patch(item.id, {
      value: item.standardValue ?? item.value,
      ...(hasDetail ? { detail: item.standardDetail ?? '' } : {}),
    });

  const remove = (id: string) => onChange(items.filter((item) => item.id !== id));

  const add = () => {
    const id = `${idPrefix}-custom-${Date.now().toString(36)}`;
    onChange([
      ...items,
      {
        id,
        standardValue: null,
        value: '',
        ...(hasDetail ? { standardDetail: null, detail: '' } : {}),
        state: 'CUSTOM',
        included: true,
      },
    ]);
    setExpanded(id);
  };

  const anyExcluded = items.some((item) => !item.included && item.standardValue !== null);

  const counts = items.reduce(
    (acc, item) => {
      if (!item.included) return acc;
      acc[stateOf(item)] += 1;
      return acc;
    },
    { STANDARD: 0, MODIFIED: 0, CUSTOM: 0 } as Record<ItemState, number>,
  );

  return (
    <Card
      title={title}
      description={description}
      actions={
        <div className="flex items-center gap-3">
          <span className="flex items-center gap-1.5 text-[11px] text-muted">
            {counts.STANDARD} standard
            {counts.MODIFIED > 0 && (
              <span className="text-amber-800">· {counts.MODIFIED} edited</span>
            )}
            {counts.CUSTOM > 0 && <span className="text-sky-800">· {counts.CUSTOM} custom</span>}
          </span>
          {onRestoreSection && (counts.MODIFIED > 0 || counts.CUSTOM > 0 || anyExcluded) && (
            <Button
              variant="ghost"
              size="sm"
              disabled={disabled}
              onClick={onRestoreSection}
              title="Put every item in this section back to the approved template"
            >
              Restore section
            </Button>
          )}
        </div>
      }
    >
      <ul className="divide-y divide-rule">
        {items.map((item) => {
          const state = stateOf(item);
          const open = expanded === item.id;
          return (
            <li key={item.id} className="py-2.5">
              <div className="flex items-start gap-3">
                <input
                  type="checkbox"
                  checked={item.included}
                  disabled={disabled}
                  aria-label={`Include ${item.value || 'new item'}`}
                  onChange={(event) => patch(item.id, { included: event.target.checked })}
                  className="mt-1 h-4 w-4 shrink-0 accent-teal"
                />

                <div className={['min-w-0 flex-1', item.included ? '' : 'opacity-55'].join(' ')}>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-[13px] text-ink">
                      {item.value || <span className="text-muted">Untitled</span>}
                    </span>
                    <StateBadge state={state} />
                  </div>
                  {hasDetail && item.detail && (
                    <p className="mt-0.5 text-[12px] text-muted">{item.detail}</p>
                  )}
                </div>

                <div className="flex shrink-0 items-center gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={disabled}
                    aria-expanded={open}
                    onClick={() => setExpanded(open ? null : item.id)}
                  >
                    {open ? 'Done' : 'Edit'}
                  </Button>
                  {state === 'MODIFIED' && (
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={disabled}
                      onClick={() => revert(item)}
                    >
                      Restore standard
                    </Button>
                  )}
                  {state === 'CUSTOM' && (
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={disabled}
                      onClick={() => remove(item.id)}
                    >
                      Remove custom item
                    </Button>
                  )}
                </div>
              </div>

              {open && (
                <div className="mt-3 space-y-3 rounded-md bg-tint p-3">
                  <label className="block">
                    <span className="mb-1 block text-[12px] font-medium text-ink">
                      {valueLabel}
                    </span>
                    <Input
                      value={item.value}
                      maxLength={400}
                      disabled={disabled}
                      autoFocus
                      onChange={(event) => patch(item.id, { value: event.target.value })}
                    />
                  </label>

                  {aiStatus?.enabled && proposalId && aiFunctions?.length ? (
                    <AIAssist
                      proposalId={proposalId}
                      status={aiStatus}
                      label="Optional AI assistance"
                      functions={aiFunctions}
                      currentText={item.value}
                      disabled={disabled}
                      onApply={(text) => patch(item.id, { value: text })}
                    />
                  ) : null}

                  {hasDetail && (
                    <label className="block">
                      <span className="mb-1 block text-[12px] font-medium text-ink">
                        {detailLabel ?? 'Description'}
                      </span>
                      <Input
                        value={item.detail ?? ''}
                        maxLength={600}
                        disabled={disabled}
                        onChange={(event) => patch(item.id, { detail: event.target.value })}
                      />
                    </label>
                  )}

                  {item.standardValue !== null && state === 'MODIFIED' && (
                    <p className="text-[12px] text-amber-800">
                      Approved wording: “{item.standardValue}”
                    </p>
                  )}
                </div>
              )}
            </li>
          );
        })}
      </ul>

      <div className="mt-3 border-t border-rule pt-3">
        <Button variant="secondary" size="sm" onClick={add} disabled={disabled}>
          {addLabel}
        </Button>
      </div>
    </Card>
  );
}
